import { FilesetResolver, ObjectDetector } from "@mediapipe/tasks-vision";
import throttle from "lodash.throttle";
import { retry } from "@/Utils/retry";
import { logInfo, logError, logWarning } from "@/Utils/logger";
import { testWebGLShaderSupport } from "@/Utils/gpuHelpers";
import { personBoxFrom, type PersonBox } from "./personBox";

export class ObjectDetectionManager {
  private static instance: ObjectDetectionManager;
  private detector: ObjectDetector | null = null;
  isRunning = false;
  private isWarmedUp = false;

  private constructor() {}

  static getInstance(): ObjectDetectionManager {
    if (!ObjectDetectionManager.instance) {
      ObjectDetectionManager.instance = new ObjectDetectionManager();
    }
    return ObjectDetectionManager.instance;
  }

  async initialize(): Promise<void> {
    if (this.detector) {
      logInfo("ObjectDetectionManager already initialized.");
      return;
    }
    logInfo("ObjectDetectionManager initialization started.");

    const initAttempt = async () => {
      const vision = await FilesetResolver.forVisionTasks(
        "https://storage.googleapis.com/truetoform-public/models/mediapipe/tasks-vision/wasm"
      );
      
      let delegate: "GPU" | "CPU" = testWebGLShaderSupport() ? "GPU" : "CPU";
      logInfo(`Initializing ObjectDetector with delegate: ${delegate}`);
      
      try {
        this.detector = await ObjectDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/truetoform-public/models/efficientdet/lite0/float16/1/efficientdet_lite0.tflite",
            delegate,
          },
          maxResults: 1,
          scoreThreshold: 0.5,
          runningMode: "VIDEO",
        });
      } catch (delegateError: any) {
        // If GPU delegate fails (common on Safari/iOS), fall back to CPU
        if (delegate === "GPU") {
          logWarning("GPU delegate failed, falling back to CPU", {
            errorMessage: delegateError?.message || "Unknown error"
          });
          delegate = "CPU";
          this.detector = await ObjectDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                "https://storage.googleapis.com/truetoform-public/models/efficientdet/lite0/float16/1/efficientdet_lite0.tflite",
              delegate,
            },
            maxResults: 1,
            scoreThreshold: 0.5,
            runningMode: "VIDEO",
          });
        } else {
          throw delegateError;
        }
      }

      await this.warmupModel();
    };

    try {
      await retry(initAttempt, {
        retries: 3,
        delayMs: 1000,
        onRetry: (attempt, error) => {
          logWarning(
            `ObjectDetectionManager initialization attempt ${attempt} failed. Retrying...`,
            // @ts-ignore
            { errorMessage: error?.message || "Unknown error" }
          );
        },
      });
      logInfo("ObjectDetectionManager initialization completed successfully.");
    } catch (error) {
      logError(
        "ObjectDetectionManager initialization failed after all retries.",
        // @ts-ignore
        { errorMessage: error?.message || "Unknown error" }
      );
      this.detector = null; // Cleanup
      throw error; // Propagate the error
    }
  }

  private async warmupModel(): Promise<void> {
    if (this.isWarmedUp) return;
    logInfo("Warming up object detector...");
    try {
      if (!this.detector) {
        throw new Error("Object detector not available for warmup.");
      }
      // Create a small blank canvas to use as a warmup image
      // Safari/iOS compatibility: Use OffscreenCanvas if available, otherwise fall back to HTMLCanvasElement
      let canvas: OffscreenCanvas | HTMLCanvasElement;
      if (typeof OffscreenCanvas !== 'undefined') {
        canvas = new OffscreenCanvas(640, 480);
      } else {
        canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 480;
      }
      const ctx = canvas.getContext("2d") as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
      if (ctx) {
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);

      if (!imageData) {
        throw new Error("Failed to create warmup image");
      }
      this.detector.detectForVideo(imageData, performance.now());
      this.isWarmedUp = true;
      logInfo("Object detector warmed up successfully.");
    } catch (error) {
      logError("Error warming up object detector.", {
        // @ts-ignore
        errorMessage: error?.message || "Unknown error",
      });
      throw error; // Re-throw to be caught by the caller
    }
  }

  // Use a lower throttle rate for better performance
  processFrame = throttle(
    async (frame: HTMLVideoElement | ImageData, timestamp: number) => {
      if (!this.detector || !this.isRunning) return;

      // Wrap the detection in a microtask to make it non-blocking
      return new Promise<
        | { frameHeight: number; personHeight: number; frameWidth: number; personBox: PersonBox }
        | undefined
      >((resolve) => {
        // Use setTimeout with 0ms delay instead of requestAnimationFrame
        // This can sometimes be more efficient for heavy computational tasks
        setTimeout(() => {
          try {
            if (!this.detector || !this.isRunning) {
              resolve(undefined);
              return;
            }

            const result = this.detector.detectForVideo(frame, timestamp);
            // Guards an empty `categories` array, which the old
            // `d.categories[0].categoryName` lookup would have thrown on.
            const personBox = personBoxFrom(result.detections);

            if (personBox) {
              const imageHeight = frame.height
                ? frame.height
                : "videoHeight" in frame
                ? frame.videoHeight
                : 0;

              const imageWidth = frame.width
                ? frame.width
                : "videoWidth" in frame
                ? frame.videoWidth
                : 0;

              resolve({
                frameHeight: imageHeight,
                personHeight: personBox.height,
                frameWidth: imageWidth,
                personBox,
              });
            } else {
              resolve(undefined);
            }
          } catch (error) {
            console.error("Error processing frame:", error);
            resolve(undefined);
          }
        }, 0);
      });
    },
    1000 / 8, // Reduce to 8 FPS for better performance
    { trailing: false, leading: true } // Ensure we process the first frame immediately
  );

  isReady(): boolean {
    return this.isWarmedUp && this.detector !== null;
  }

  start(): void {
    if (this.isReady()) {
      this.isRunning = true;
    }
  }

  pause(): void {
    this.isRunning = false;
  }

  terminate(): void {
    this.detector?.close();
    this.detector = null;
    this.isRunning = false;
    this.isWarmedUp = false;
  }
}
